import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { NoWhiteSpaceValidator } from 'src/app/components/core/_infrastructure/classes/noWhiteSpaceValidator';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from '../../../../../core/components/navmenu/navmenu.service';
import { GlobalService } from '../../../../../core/_infrastructure/classes/services/global.service';
import { EnumSiNo, Tc } from '../../../../../core/_infrastructure/types/common';
import { EnumIdsTcTipoSolicitudCam } from '../../../../../_campanias/types/enum';
import { EnumCodigosTwTcAcciones } from '../../../../../_importacion/solicitudes/types/enum';
import { PeriodoCamService } from '../periodocam.service';
import { PeriodoCamViewModel, PeriodoTipoSolicitudCamViewModel } from '../periodocam.types';
import { PeriodoPlazoService } from '../../plazos/plazos.service';
import { ListaPlazosPeriodoCamViewModel } from '../../plazos/plazos.types';

@Component({
    selector: 'app-periodocam-wrapper-edit',
    templateUrl: './periodocam-wrapper-edit.component.html',
    styleUrls: ['./periodocam-wrapper-edit.component.scss'],
})
export class PeriodoCamWrapperEditComponent extends ComponentBase implements OnInit {
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    visibilitySpinner = false;
    periodoCam: PeriodoCamViewModel;
    campanias: Tc[];
    habilitarEdicion = true;

    listaPlazosPeriodo: ListaPlazosPeriodoCamViewModel;
    esVigenteConSolicitudes = false;
    esPlazoExportacionVigente = false;

    successMessage = 'Periodo guardado correctamente';
    private listUrl = '/mantenimiento/periodos';
    private editionUrl = '/mantenimiento/periodos/datosbasicos/edit/';

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const id: any = params.get('id');
        if (id) {
            this.getPeriodoCam(id);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private periodoCamService: PeriodoCamService,
        private plazosService: PeriodoPlazoService,
        protected notificationService: NotificationService,
        private globalService: GlobalService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
        this.habilitarEdicion = this.loggedInUser!.getAction(EnumCodigosTwTcAcciones.MENU_DATOS_BASICOS_PERIODO).Editable;
        if (!this.habilitarEdicion) {
            this.DisableControlsConsulta();
        }
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.getCampanias();

        this.form = this.fb.group({
            Id: [0],
            AmbitoNacional: [false], // Not visible but needed
            FechaInicio: [null, Validators.required],
            FechaFin: [null, Validators.required],
            IdCampania: [null, Validators.required],
            DescripcionPeriodo: [null, Validators.compose([Validators.required, NoWhiteSpaceValidator.cannotContainSpace])],
            SuperficieMax: [null],
            Dextrio: [null],
            Parcela: [false],
            Confeccion: [false],
            Invernadero: [false],
            EmpresasDeshidratacion: [false],
            PresentarSolicitudesFueraPeriodo: [false],
            ControlProduccionExportacion: [false],
            HabilitadaTemporalmente: [false],
            CicloCortoInspParcela: [false],
            CicloCortoInspConfeccion: [false],
            CicloCortoInspInvernadero: [false],
            CicloCortoInspEmpresasDeshidratacion: [false],
            RequiereFirmaInspectorPaisDestino: [false],
            AvisoConfeccion: [false],
            RequiereAvisoFechaConfeccion: [false],
            TamanioMaximoAgrupacion: [false],
            CertificaKgs: [false],
        });
    }

    private DisableControlsConsulta(): void {
        this.form.controls['Parcela'].disable();
        this.form.controls['Confeccion'].disable();
        this.form.controls['Invernadero'].disable();
        this.form.controls['EmpresasDeshidratacion'].disable();
        this.form.controls['FechaInicio'].disable();
        this.form.controls['FechaFin'].disable();
        this.form.controls['IdCampania'].disable();
        this.form.controls['DescripcionPeriodo'].disable();
        this.form.controls['SuperficieMax'].disable();
        this.form.controls['Dextrio'].disable();

        this.form.controls['PresentarSolicitudesFueraPeriodo'].disable();
        this.form.controls['ControlProduccionExportacion'].disable();
        this.form.controls['HabilitadaTemporalmente'].disable();

        this.form.controls['CicloCortoInspParcela'].disable();
        this.form.controls['CicloCortoInspConfeccion'].disable();
        this.form.controls['CicloCortoInspInvernadero'].disable();
        this.form.controls['CicloCortoInspEmpresasDeshidratacion'].disable();

        this.form.controls['RequiereFirmaInspectorPaisDestino'].disable();
        this.form.controls['RequiereAvisoFechaConfeccion'].disable();

        this.form.controls['AvisoConfeccion'].disable();
        this.form.controls['TamanioMaximoAgrupacion'].disable();
        this.form.controls['CertificaKgs'].disable();
    }

    private disableControlsSiVigente(): void {
        if (!this.habilitarEdicion || !this.esVigenteConSolicitudes) {
            return;
        }
        this.form.controls['Parcela'].disable();
        this.form.controls['Confeccion'].disable();
        this.form.controls['Invernadero'].disable();
        this.form.controls['EmpresasDeshidratacion'].disable();
        this.form.controls['FechaInicio'].disable();
        this.form.controls['FechaFin'].disable();
        this.form.controls['IdCampania'].disable();
        this.form.controls['DescripcionPeriodo'].disable();
        this.form.controls['SuperficieMax'].disable();
        this.form.controls['Dextrio'].disable();

        this.form.controls['CicloCortoInspParcela'].disable();
        this.form.controls['CicloCortoInspConfeccion'].disable();
        this.form.controls['CicloCortoInspInvernadero'].disable();
        this.form.controls['CicloCortoInspEmpresasDeshidratacion'].disable();

        this.form.controls['TamanioMaximoAgrupacion'].disable();
        this.form.controls['CertificaKgs'].disable();
    }

    private getCampanias(): void {
        this.globalService.getAllCampanias().subscribe((campanias: Tc[]) => {
            this.campanias = campanias;
        });
    }

    /**
     * Método que invoca a la API para cargar los datos de un periodo a partir de su id.
     * @param id number
     * @return void
     */
    private getPeriodoCam(id: number): void {
        this.periodoCamService
            .getById(id)
            .pipe(
                tap((p: PeriodoCamViewModel) => {
                    this.periodoCam = p;
                    this.periodoCamService.periodoCam = this.periodoCam;
                    this.navMenuService.updateMenuServiceForPeriodo(p.Id!);
                    if (this.esPeriodoFinalizado) {
                        this.form.disable();
                        this.form.controls['HabilitadaTemporalmente'].enable();
                        this.form.controls['PresentarSolicitudesFueraPeriodo'].enable();
                        return;
                    }
                    this.getSiVigenteConSolicitudes(id);
                    this.getPlazos(id);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Método que invoca a la API para comprueba si el periodo es vigente y tiene solicitudes de campaña asociadas.
     * @param id number
     * @return void
     */
    private getSiVigenteConSolicitudes(id: number): void {
        this.periodoCamService
            .getPeriodoEsVigente(id)
            .pipe(
                tap((p: boolean) => {
                    this.esVigenteConSolicitudes = p;
                    this.disableControlsSiVigente();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Obtenemos los plazos del periodo, para saber si hay alguno que ya haya empezado.
     * Si hay alguno empezado se deshabilitado el campo ControlProduccionExportacion.
     * @param idPeriodo number
     */
    private getPlazos(idPeriodo: number): void {
        this.plazosService
            .getListaPlazosDePeriodo(idPeriodo)
            .pipe(
                tap((results: ListaPlazosPeriodoCamViewModel) => {
                    this.listaPlazosPeriodo = results;
                    this.checkIfPlazoExportacionVigente();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Comprobamos los plazos de exportación de confección, invernadero, parcela o emp. deshidratadora, para cada plazo del periodo.
     * Si alguno tiene una fecha de inicio de exportación anterior al día actual, se deshabilita el botón "ControlProduccionExportacion"
     * @returns void
     */
    private checkIfPlazoExportacionVigente(): void {
        const hoy = new Date();
        //se inserta el ordinal
        for (let i = 0; i < this.listaPlazosPeriodo.PlazoPeriodoCollection.length; i++) {
            const plazoPeriodo = this.listaPlazosPeriodo.PlazoPeriodoCollection[i];
            if (plazoPeriodo) {
                if (plazoPeriodo.FechaInicioExportacionConfeccion) {
                    const fiEC = new Date(plazoPeriodo.FechaInicioExportacionConfeccion);
                    if (hoy >= fiEC) {
                        this.esPlazoExportacionVigente = true;
                        break;
                    }
                }
                if (plazoPeriodo.FechaInicioExportacionInvernadero) {
                    const fiEI = new Date(plazoPeriodo.FechaInicioExportacionInvernadero);
                    if (hoy >= fiEI) {
                        this.esPlazoExportacionVigente = true;
                        break;
                    }
                }
                if (plazoPeriodo.FechaInicioExportacionParcela) {
                    const fiEP = new Date(plazoPeriodo.FechaInicioExportacionParcela);
                    if (hoy >= fiEP) {
                        this.esPlazoExportacionVigente = true;
                        break;
                    }
                }
                if (plazoPeriodo.FechaInicioExportacionEmpDeshidratadoras) {
                    const fiEED = new Date(plazoPeriodo.FechaInicioExportacionEmpDeshidratadoras);
                    if (hoy >= fiEED) {
                        this.esPlazoExportacionVigente = true;
                        break;
                    }
                }
            }
        }
        if (this.esPlazoExportacionVigente) {
            this.form.controls['ControlProduccionExportacion'].disable();
            this.form.controls['RequiereFirmaInspectorPaisDestino'].disable();
            this.form.controls['AvisoConfeccion'].disable();
            this.form.controls['RequiereAvisoFechaConfeccion'].disable();
        }
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el periodo
     * @return void
     */
    onSave(): void {
        //validación tipo Periodo
        const model = this.form.getRawValue();

        if (!model.Parcela && !model.Confeccion && !model.Invernadero && !model.EmpresasDeshidratacion) {
            this.addErrorMessage('Debe seleccionar algún tipo para el Periodo: Parcela, Confección, Invernadero o Empresas Deshidratación');
            return;
        }
        this.visibilitySpinner = true;

        model.Dextrio = model?.Dextrio != null ? model?.Dextrio.replace('.', '').replace(',', '.') : model?.Dextrio;

        //MAPEO PERIODO TIPOS
        model.TPeriodoTipoSolicitudCamCollection = [];
        if (model.Parcela) {
            const tipoSolicitudCam = new PeriodoTipoSolicitudCamViewModel();
            tipoSolicitudCam.IdTipoSolicitud = EnumIdsTcTipoSolicitudCam.ID_PARCELA;
            model.TPeriodoTipoSolicitudCamCollection.push(tipoSolicitudCam);
        } else {
            model.CicloCortoInspParcela = EnumSiNo.NO;
        }

        if (model.Confeccion) {
            const tipoSolicitudCam = new PeriodoTipoSolicitudCamViewModel();
            tipoSolicitudCam.IdTipoSolicitud = EnumIdsTcTipoSolicitudCam.ID_CONFECCION;
            model.TPeriodoTipoSolicitudCamCollection.push(tipoSolicitudCam);
        } else {
            model.CicloCortoInspConfeccion = EnumSiNo.NO;
        }

        if (model.EmpresasDeshidratacion) {
            const tipoSolicitudCam = new PeriodoTipoSolicitudCamViewModel();
            tipoSolicitudCam.IdTipoSolicitud = EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION;
            model.TPeriodoTipoSolicitudCamCollection.push(tipoSolicitudCam);
        } else {
            model.CicloCortoInspEmpresasDeshidratacion = EnumSiNo.NO;
        }

        if (model.Invernadero) {
            const tipoSolicitudCam = new PeriodoTipoSolicitudCamViewModel();
            tipoSolicitudCam.IdTipoSolicitud = EnumIdsTcTipoSolicitudCam.ID_INVERNADERO;
            model.TPeriodoTipoSolicitudCamCollection.push(tipoSolicitudCam);
        } else {
            model.CicloCortoInspInvernadero = EnumSiNo.NO;
        }

        this.periodoCamService
            .save(model)
            .pipe(
                tap(res => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    if (this.periodoCam?.Id) {
                        this.getPeriodoCam(this.periodoCam?.Id);
                        return;
                    } else {
                        this.gotoEdition(res);
                    }
                }),
                catchError(error => {
                    this.visibilitySpinner = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    private gotoEdition(IdPeriodo: number): void {
        this.router.navigate([this.editionUrl, IdPeriodo]);
    }

    get formInvalid(): boolean {
        return !this.form.valid || !this.form.dirty || this.form.pristine;
    }

    get esPeriodoIniciado(): boolean {
        const hoy = new Date();
        const fechaInicioPeriodo = new Date(this.periodoCam?.FechaInicio);
        return hoy >= fechaInicioPeriodo;
    }

    get esPeriodoFinalizado(): boolean {
        const hoy = new Date();
        const fechaFinPeriodo = new Date(this.periodoCam?.FechaFin);
        return hoy >= fechaFinPeriodo;
    }
}
